import { useEffect, useMemo, useRef, useState } from 'react';
import { Download, Trash2, X } from 'lucide-react';
import { log, type LogCategory, type LogEntry, type LogLevel } from '@/lib/logger';
import { cn } from '@/lib/utils';

const CATEGORY_COLORS: Record<LogCategory, string> = {
  action: 'var(--color-accent)',
  command: 'var(--color-amber)',
  ws: '#a78bfa',
  router: '#7dd3fc',
  mode: '#6ee7b7',
  auth: '#fbbf24',
  select: '#c4b5fd',
  mission: '#f59e0b',
  error: 'var(--color-status-critical)',
};

const ALL_CATEGORIES: LogCategory[] = [
  'action',
  'command',
  'ws',
  'router',
  'mode',
  'auth',
  'select',
  'mission',
  'error',
];

/**
 * Floating timeline of every operator interaction the app captured.
 * Toggle with Ctrl+Shift+L. Filter by category, expand entries to see
 * payloads, clear or download the buffer.
 */
export function LogViewer() {
  const [open, setOpen] = useState(false);
  const [, force] = useState(0);
  const [active, setActive] = useState<Set<LogCategory>>(() => new Set(ALL_CATEGORIES));
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());
  const [paused, setPaused] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Re-render on every new entry so the timeline stays live. Cheap because
  // ring buffer is small (1000 entries max) and we render only filtered.
  useEffect(() => {
    if (paused) return;
    return log.subscribe(() => force((n) => n + 1));
  }, [paused]);

  // Auto-scroll to the newest entry unless the user has scrolled up.
  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (!el) return;
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (isAtBottom) el.scrollTop = el.scrollHeight;
  });

  // Toggle keyboard shortcut: Ctrl+Shift+L.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!(e.ctrlKey && e.shiftKey)) return;
      if (e.key.toLowerCase() !== 'l') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      e.preventDefault();
      setOpen((v) => !v);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const entries = useMemo(() => {
    return log.history().filter((e) => active.has(e.category));
  }, [active, open, force]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  function toggleCat(c: LogCategory) {
    setActive((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  }

  function toggleExpand(id: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div
      className="fixed bottom-3 right-3 z-[100] flex max-h-[60vh] w-[calc(100vw-1.5rem)] max-w-[520px] flex-col overflow-hidden rounded-md border border-border-default bg-surface-1/95 shadow-[var(--shadow-pop)] backdrop-blur-md"
      role="dialog"
      aria-label="Activity log"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border-default px-3 py-2">
        <div className="flex items-center gap-2">
          <span
            className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em]"
            style={{ color: 'var(--color-accent)' }}
          >
            Activity Log
          </span>
          <span className="font-mono text-[10px] text-fg-muted">{entries.length}</span>
          {paused && (
            <span
              className="rounded border px-1 font-mono text-[9px] uppercase tracking-[0.1em]"
              style={{
                color: 'var(--color-amber)',
                borderColor: 'var(--color-amber)',
              }}
            >
              paused
            </span>
          )}
        </div>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => setPaused((v) => !v)}
            className="rounded px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.1em] text-fg-secondary hover:bg-surface-2 hover:text-fg-primary"
            title="Pause / resume live tail"
          >
            {paused ? 'Resume' : 'Pause'}
          </button>
          <button
            type="button"
            onClick={() => log.download()}
            className="grid h-6 w-6 place-items-center rounded text-fg-secondary hover:bg-surface-2 hover:text-fg-primary"
            title="Download log as JSON"
            aria-label="Download log"
          >
            <Download className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => {
              log.clear();
              force((n) => n + 1);
            }}
            className="grid h-6 w-6 place-items-center rounded text-fg-secondary hover:bg-surface-2 hover:text-fg-primary"
            title="Clear buffer"
            aria-label="Clear"
          >
            <Trash2 className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="grid h-6 w-6 place-items-center rounded text-fg-secondary hover:bg-surface-2 hover:text-fg-primary"
            title="Close (Ctrl+Shift+L)"
            aria-label="Close"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-1 border-b border-border-subtle px-3 py-1.5">
        {ALL_CATEGORIES.map((c) => {
          const on = active.has(c);
          return (
            <button
              key={c}
              type="button"
              onClick={() => toggleCat(c)}
              className={cn(
                'flex items-center gap-1 rounded-sm border px-1.5 py-[1px] font-mono text-[9px] uppercase tracking-[0.1em] transition-colors',
                on
                  ? 'border-border-default text-fg-primary'
                  : 'border-border-subtle text-fg-muted hover:text-fg-secondary',
              )}
            >
              <span
                className="h-2 w-2 rounded-full"
                style={{ background: CATEGORY_COLORS[c], opacity: on ? 1 : 0.3 }}
              />
              {c}
            </button>
          );
        })}
      </div>

      {/* Entries */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto font-mono text-[11px]"
      >
        {entries.length === 0 ? (
          <p className="px-4 py-6 text-center text-fg-muted">
            No events. Interact with the app to populate the timeline.
          </p>
        ) : (
          entries.map((e) => (
            <LogRow
              key={e.id}
              entry={e}
              expanded={expanded.has(e.id)}
              onToggle={() => toggleExpand(e.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function LogRow({
  entry,
  expanded,
  onToggle,
}: {
  entry: LogEntry;
  expanded: boolean;
  onToggle: () => void;
}) {
  const hasData = entry.data !== undefined;
  const levelStyle = levelTone(entry.level);
  return (
    <div className="border-b border-border-subtle/60 px-3 py-1 leading-tight hover:bg-surface-2/40">
      <button
        type="button"
        onClick={onToggle}
        disabled={!hasData}
        className={cn(
          'flex w-full items-baseline gap-2 text-left',
          !hasData && 'cursor-default',
        )}
      >
        <span className="shrink-0 text-fg-muted">{fmtTime(entry.ts)}</span>
        <span
          className="shrink-0 rounded-sm px-1 text-[9px] uppercase tracking-[0.1em]"
          style={{
            background: 'rgba(255,255,255,0.04)',
            color: CATEGORY_COLORS[entry.category],
          }}
        >
          {entry.category}
        </span>
        <span className="min-w-0 flex-1 truncate" style={{ color: levelStyle }}>
          {entry.message}
        </span>
        {hasData && (
          <span className="shrink-0 text-fg-muted">{expanded ? '▾' : '▸'}</span>
        )}
      </button>
      {hasData && expanded && (
        <pre className="ml-[68px] mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded border border-border-subtle bg-surface-2 px-2 py-1 text-[10px] text-fg-secondary">
          {JSON.stringify(entry.data, null, 2)}
        </pre>
      )}
    </div>
  );
}

function fmtTime(ts: number): string {
  const d = new Date(ts);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  const ms = String(d.getMilliseconds()).padStart(3, '0');
  return `${hh}:${mm}:${ss}.${ms}`;
}

function levelTone(level: LogLevel): string {
  switch (level) {
    case 'warn':
      return 'var(--color-status-warn)';
    case 'error':
      return 'var(--color-status-critical)';
    default:
      return 'var(--color-fg-primary)';
  }
}
